import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Plus, Edit, Trash, Search, X, Layers, RefreshCw, Calendar, DollarSign, ChevronLeft, ChevronRight, Eye, Printer, FileText, Package, AlertCircle, CheckCircle, TrendingUp, Upload, Download, FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import authUtils from '../utils/authUtils';

const XuatPhuPhamManagement = () => {
    // ================= STATE MANAGEMENT =================
    const [phieuList, setPhieuList] = useState([]);
    const [phuPhamList, setPhuPhamList] = useState([]); // Master data: PHU_PHAM
    const [customers, setCustomers] = useState([]); // Customer list
    const [isLoading, setIsLoading] = useState(false);
    const [search, setSearch] = useState('');

    // Modal & Form States
    const [showModal, setShowModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Filter & Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortConfig, setSortConfig] = useState({ key: 'NGAY_XUAT', direction: 'descending' });
    const [showImportModal, setShowImportModal] = useState(false);
    const [importPreview, setImportPreview] = useState(null);

    // Filter Date Range
    const [dateRange, setDateRange] = useState(() => {
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

        // Format to YYYY-MM-DD manually to avoid UTC conversion issues
        const formatDate = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };

        return {
            startDate: formatDate(firstDay),
            endDate: formatDate(today)
        };
    });

    // Current Voucher State (Parent: XUAT_PHUPHAM)
    const [currentPhieu, setCurrentPhieu] = useState(() => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');

        return {
            'SOPHIEU': '',
            'NGAY_XUAT': `${year}-${month}-${day}`,
            'NCC_KHACHHANG': '',
            'TONG_KHOILUONG': 0,
            'THANHTIEN': 0,
            'PT VAT': 0,
            'TIEN VAT': 0,
            'TONGTIEN': 0,
            'GHICHU': ''
        };
    });

    // Details State (Child: XUAT_PHUPHAM_CT)
    const [chiTietList, setChiTietList] = useState([]);

    // (Removed separate currentChiTiet state as we use direct table editing)


    // Delete Confirmation
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [phieuToDelete, setPhieuToDelete] = useState(null);

    // Refs for dropdowns
    const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
    const [customerSearchTerm, setCustomerSearchTerm] = useState('');
    const [activeProductDropdownIndex, setActiveProductDropdownIndex] = useState(null); // Track active row for product search

    // ================= INITIALIZATION =================
    useEffect(() => {
        fetchData();
        fetchPhuPhamList();
        fetchCustomers();
    }, []);

    const fetchData = async () => {
        try {
            setIsLoading(true);
            const response = await authUtils.apiRequest('XUAT_PHUPHAM', 'Find', {});
            setPhieuList(Array.isArray(response) ? response : []);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Lỗi khi tải danh sách phiếu xuất');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchPhuPhamList = async () => {
        try {
            const response = await authUtils.apiRequest('PHU_PHAM', 'Find', {});
            // Filter active items if needed, or just take all for dropdown
            setPhuPhamList(Array.isArray(response) ? response : []);
        } catch (error) {
        }
    };

    const fetchCustomers = async () => {
        try {
            // Assuming we use DSKH table or similar
            const response = await authUtils.apiRequestKHO('DSKH', 'Find', {});
            setCustomers(Array.isArray(response) ? response : []);
        } catch (error) {
        }
    };

    // ================= HELPERS =================
    const formatCurrency = (value) => {
        if (!value) return '0 ₫';
        return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(value);
    };

    const formatNumber = (value, digits = 2) => {
        if (value === undefined || value === null || value === '') return '0';
        return new Intl.NumberFormat('vi-VN', { minimumFractionDigits: 0, maximumFractionDigits: digits }).format(value);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('vi-VN');
    };

    // ================= LOGIC HANDLERS =================

    // --- Editable Table Handlers ---
    const handleDetailChange = (index, field, value) => {
        const newList = [...chiTietList];
        newList[index] = { ...newList[index], [field]: value };

        // Auto Calc
        if (field === 'KHOILUONG' || field === 'DONGIA') {
            const kl = parseFloat(newList[index].KHOILUONG) || 0;
            const dg = parseFloat(newList[index].DONGIA) || 0;
            newList[index].THANHTIEN = kl * dg;
        }

        // Auto Fill Price
        if (field === 'TEN_HANG') {
            const product = phuPhamList.find(p => p.TEN_HANG === value);
            if (product) {
                newList[index].DONGIA = parseFloat(product.DONGIA) || 0;
                // Recalc thanh tien
                const kl = parseFloat(newList[index].KHOILUONG) || 0;
                newList[index].THANHTIEN = kl * parseFloat(product.DONGIA);
            }
        }

        setChiTietList(newList);
    };

    const handleAddEmptyRow = () => {
        setChiTietList([...chiTietList, {
            'TEN_HANG': '',
            'KHOILUONG': '',
            'DONGIA': 0,
            'THANHTIEN': 0,
            'GHICHU': ''
        }]);
    };

    const handleRemoveChiTiet = (index) => {
        const newList = [...chiTietList];
        newList.splice(index, 1);
        setChiTietList(newList);
    };

    // Sorting Helper
    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    // --- Parent Calculation (Auto Update from Details) ---
    useEffect(() => {
        const tongKhoiLuong = chiTietList.reduce((sum, item) => sum + (parseFloat(item.KHOILUONG) || 0), 0);
        const thanhTien = chiTietList.reduce((sum, item) => sum + (parseFloat(item.THANHTIEN) || 0), 0);

        const ptVAT = parseFloat(currentPhieu['PT VAT']) || 0;
        const tienVAT = Math.round(thanhTien * (ptVAT / 100));
        const tongTien = thanhTien + tienVAT;

        setCurrentPhieu(prev => ({
            ...prev,
            'TONG_KHOILUONG': tongKhoiLuong,
            'THANHTIEN': thanhTien,
            'TIEN VAT': tienVAT,
            'TONGTIEN': tongTien
        }));
    }, [chiTietList, currentPhieu['PT VAT']]);


    // --- CRUD Operations ---

    const handleOpenModal = async (phieu = null) => {
        if (phieu) {
            setIsEditMode(true);
            // Format Date safely to YYYY-MM-DD local
            const formatDateSafe = (dStr) => {
                const d = dStr ? new Date(dStr) : new Date();
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            setCurrentPhieu({
                ...phieu,
                'NGAY_XUAT': phieu['NGAY_XUAT'] ? formatDateSafe(phieu['NGAY_XUAT']) : formatDateSafe(new Date())
            });
            setCustomerSearchTerm(phieu['NCC_KHACHHANG'] || '');

            // Fetch details for this voucher
            try {
                // Assuming XUAT_PHUPHAM_CT has a field SOPHIEU related to Parent
                const allDetails = await authUtils.apiRequest('XUAT_PHUPHAM_CT', 'Find', {});
                const relatedDetails = allDetails.filter(d => d.SOPHIEU === phieu.SOPHIEU);
                setChiTietList(relatedDetails);
            } catch (error) {
                console.error("Error fetching details", error);
                setChiTietList([]);
            }
        } else {
            setIsEditMode(false);
            const today = new Date();
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const day = String(today.getDate()).padStart(2, '0');

            setCurrentPhieu({
                'SOPHIEU': '', // Auto generated
                'NGAY_XUAT': `${year}-${month}-${day}`,
                'NCC_KHACHHANG': '',
                'TONG_KHOILUONG': 0,
                'THANHTIEN': 0,
                'PT VAT': 0,
                'TIEN VAT': 0,
                'TONGTIEN': 0,
                'GHICHU': ''
            });
            setChiTietList([{ // Add one empty row by default
                'TEN_HANG': '',
                'KHOILUONG': '',
                'DONGIA': 0,
                'THANHTIEN': 0,
                'GHICHU': ''
            }]);
            setCustomerSearchTerm('');
        }
        setShowModal(true);
    };

    const handleSave = async () => {
        if (!currentPhieu.NCC_KHACHHANG) {
            toast.error('Vui lòng chọn khách hàng');
            return;
        }
        // Filter out empty rows (no Product Name)
        const validDetails = chiTietList.filter(d => d.TEN_HANG && d.TEN_HANG.trim() !== '');

        if (validDetails.length === 0) {
            toast.error('Vui lòng nhập ít nhất một dòng chi tiết hợp lệ');
            return;
        }

        setIsSubmitting(true);
        try {
            // NOTE:
            // Since this API might require saving Parent separately from Child, or bundled.
            // Following the pattern from XuatNhapKhoManager where we might need custom logic.
            // Assuming simplified "Add" handles parent, and we loop to add children, OR single Payload.
            // Let's assume we save Parent first to get ID (if needed) or use existing ID.

            // However, usually we send structure "Rows": [{...}]
            // If the backend doesn't support deep insert, we do: 1. Save Header, 2. Save Details.

            // For now, let's simulate the standard pattern:
            // 1. If Add: Generate SOPHIEU (or let backend), Save Parent. Then Save Children.
            // But usually we need the SOPHIEU for children.

            // Let's assume user inputs SOPHIEU or we generate it.
            // If currentPhieu.SOPHIEU is empty, let's generate a temporary one or rely on backend return?
            // "X026" style.

            // Strategy: We will just save Parent.
            // Since I don't know the exact backend capability for deep insert, I will assume:
            // - Add Parent -> Get Success
            // - Add Children linked to Parent.

            // REALITY CHECK: standard apiRequest usually just takes Rows for a single table.

            let soPhieu = currentPhieu.SOPHIEU;

            // If New Mode and no SOPHIEU, let's try to generate one locally or hope backend handles it.
            // Let's generate a simple timestamp-based ID if empty to ensure linkage (Client-side ID generation)
            if (!isEditMode && !soPhieu) {
                const date = new Date();
                // Format: XPP-YYMMDD-HHMM
                const yymmdd = date.toISOString().slice(2, 10).replace(/-/g, '');
                const hhmm = date.getHours().toString().padStart(2, '0') + date.getMinutes().toString().padStart(2, '0');
                soPhieu = `XPP-${yymmdd}-${hhmm}`;
            }

            // Check for duplicate SOPHIEU
            if (soPhieu) {
                const isDuplicate = phieuList.some(p => {
                    // If editing, allow the same SOPHIEU (no change)
                    if (isEditMode && p.SOPHIEU === currentPhieu.SOPHIEU) {
                        return false;
                    }
                    return p.SOPHIEU === soPhieu;
                });

                if (isDuplicate) {
                    toast.error(`Số phiếu "${soPhieu}" đã tồn tại. Vui lòng nhập số phiếu khác!`);
                    setIsSubmitting(false);
                    return;
                }
            }

            const parentData = {
                ...currentPhieu,
                'SOPHIEU': soPhieu
            };

            // 1. Save Parent
            if (isEditMode) {
                await authUtils.apiRequest('XUAT_PHUPHAM', 'Edit', { "Rows": [parentData] });
                // Update details: Delete old for this Phieu and Add new? Or Diff?
                // Simplest: Delete all details for this SOPHIEU, then re-add.
                // Fetch existing details first to get IDs for delete?
                // "Delete" requires PK. Assuming SOPHIEU_CT is PK.

                // Get current details from DB to find what to delete
                const oldDetails = await authUtils.apiRequest('XUAT_PHUPHAM_CT', 'Find', {});
                const myOldDetails = oldDetails.filter(d => d.SOPHIEU === soPhieu);

                if (myOldDetails.length > 0) {
                    // Delete them
                    await authUtils.apiRequest('XUAT_PHUPHAM_CT', 'Delete', { "Rows": myOldDetails.map(d => ({ 'SOPHIEU_CT': d.SOPHIEU_CT })) });
                }
            } else {
                await authUtils.apiRequest('XUAT_PHUPHAM', 'Add', { "Rows": [parentData] });
            }

            // 2. Save Children
            const childrenData = validDetails.map(item => ({
                ...item,
                'SOPHIEU': soPhieu, // Link to parent
                'SOPHIEU_CT': item.SOPHIEU_CT || `${soPhieu}_CT_${Date.now()}_${Math.floor(Math.random() * 1000)}`, // Generate ID if missing
                'GHICHU': item.GHICHU || '' // Ensure GHICHU is at least empty string
            }));

            if (childrenData.length > 0) {
                await authUtils.apiRequest('XUAT_PHUPHAM_CT', 'Add', { "Rows": childrenData });
            }

            toast.success(isEditMode ? 'Cập nhật thành công!' : 'Tạo phiếu thành công!');
            setShowModal(false);
            fetchData();

        } catch (error) {
            console.error(error);
            toast.error('Có lỗi xảy ra khi lưu phiếu');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (!phieuToDelete) return;
        try {
            // Delete Details First? Or Cascade? check backend. Safer to delete details first.
            // 1. Find details
            const allDetails = await authUtils.apiRequest('XUAT_PHUPHAM_CT', 'Find', {});
            const relatedDetails = allDetails.filter(d => d.SOPHIEU === phieuToDelete.SOPHIEU);

            if (relatedDetails.length > 0) {
                await authUtils.apiRequest('XUAT_PHUPHAM_CT', 'Delete', { "Rows": relatedDetails.map(d => ({ 'SOPHIEU_CT': d.SOPHIEU_CT })) });
            }

            // 2. Delete Parent
            await authUtils.apiRequest('XUAT_PHUPHAM', 'Delete', { "Rows": [{ 'SOPHIEU': phieuToDelete.SOPHIEU }] });

            toast.success('Xóa phiếu thành công');
            setShowDeleteConfirmation(false);
            setPhieuToDelete(null);
            fetchData();
        } catch (error) {
            console.error(error);
            toast.error('Có lỗi khi xóa phiếu');
        }
    };

    // ================= UI RENDER HELPER =================
    // Filtered Customers
    const filteredCustomers = customers.filter(c =>
        c['TEN_KHACHHANG']?.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
        c['MA_KH']?.toLowerCase().includes(customerSearchTerm.toLowerCase())
    );

    // List filtering and sorting
    const filteredItems = useMemo(() => {
        let items = [...phieuList];
        if (search) {
            const lowerSearch = search.toLowerCase();
            items = items.filter(item =>
                item.SOPHIEU?.toLowerCase().includes(lowerSearch) ||
                item.NCC_KHACHHANG?.toLowerCase().includes(lowerSearch)
            );
        }

        // Filter by Date
        if (dateRange.startDate && dateRange.endDate) {
            const start = new Date(dateRange.startDate);
            start.setHours(0, 0, 0, 0);
            const end = new Date(dateRange.endDate);
            end.setHours(23, 59, 59, 999);

            items = items.filter(item => {
                const date = new Date(item.NGAY_XUAT);
                return date >= start && date <= end;
            });
        }

        if (sortConfig.key) {
            items.sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];

                // Handle Dates
                if (sortConfig.key === 'NGAY_XUAT') {
                    return sortConfig.direction === 'ascending'
                        ? new Date(aValue) - new Date(bValue)
                        : new Date(bValue) - new Date(aValue);
                }

                // Handle Numbers
                if (typeof aValue === 'number' && typeof bValue === 'number') {
                    return sortConfig.direction === 'ascending' ? aValue - bValue : bValue - aValue;
                }

                // Default String
                if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return items;
    }, [phieuList, search, sortConfig, dateRange]);

    // Stats
    const stats = useMemo(() => {
        const totalVouchers = phieuList.length;
        const totalVolume = phieuList.reduce((sum, item) => sum + (parseFloat(item.TONG_KHOILUONG) || 0), 0);
        const totalRevenue = phieuList.reduce((sum, item) => sum + (parseFloat(item.TONGTIEN) || 0), 0);
        return { totalVouchers, totalVolume, totalRevenue };
    }, [phieuList]);



    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);


    // --- Import Helpers & Logic ---
    const handleCloseImportModal = () => {
        setShowImportModal(false);
        setImportPreview(null);
    };

    const handleImportConfirm = async () => {
        if (!importPreview || importPreview.length === 0) return;
        const validItems = importPreview.filter(item => item.isValid);
        if (validItems.length === 0) {
            toast.error("Không có phiếu nào hợp lệ để lưu!");
            return;
        }

        setIsLoading(true);
        let successCount = 0;

        try {
            for (const group of validItems) {
                let soPhieu = group.SOPHIEU;
                if (!soPhieu) {
                    const datePart = group.NGAY_XUAT.replace(/-/g, '').substring(2);
                    const randomSuffix = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
                    soPhieu = `XPP-${datePart}-${randomSuffix}`;
                }

                // Parent
                const parentData = {
                    'SOPHIEU': soPhieu,
                    'NGAY_XUAT': group.NGAY_XUAT,
                    'NCC_KHACHHANG': group.NCC_KHACHHANG,
                    'TONG_KHOILUONG': group.tongKhoiLuong,
                    'THANHTIEN': group.thanhTien,
                    'PT VAT': 0, 'TIEN VAT': 0,
                    'TONGTIEN': group.thanhTien,
                    'GHICHU': 'Imported Excel'
                };
                await authUtils.apiRequest('XUAT_PHUPHAM', 'Add', { "Rows": [parentData] });

                // Children
                const childrenData = group.details.map(d => ({
                    ...d,
                    'SOPHIEU': soPhieu,
                    'SOPHIEU_CT': `${soPhieu}_CT_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`
                }));

                if (childrenData.length > 0) {
                    await authUtils.apiRequest('XUAT_PHUPHAM_CT', 'Add', { "Rows": childrenData });
                }
                successCount++;
            }

            toast.success(`Đã lưu ${successCount} phiếu!`);
            handleCloseImportModal();
            fetchData();
        } catch (error) {
            console.error(error);
            toast.error("Lỗi lưu: " + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownloadTemplate = () => {
        const templateData = [
            {
                'SOPHIEU': '',
                'TEN_KHACHHANG': 'Công ty A',
                'NGÀY XUẤT': '17/01/2026',
                'TÊN HÀNG': 'Củi Bìa',
                'KHỐI LƯỢNG': 5.5,
                'ĐƠN GIÁ': 1000,
                'GHI CHÚ': 'Ghi chú mẫu'
            }
        ];
        const ws = XLSX.utils.json_to_sheet(templateData);
        ws['!cols'] = [{ wch: 30 }, { wch: 15 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 30 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "MauNhapXuatPhuPham");
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(data, 'Mau_Nhap_Phu_Pham.xlsx');
    };

    const handleFileUploadWrapped = (e) => {
        handleFileUpload(e);
    };

    // --- Import Excel (Start Review) ---
    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        e.target.value = null; // Reset

        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary', cellDates: true, cellNF: false, cellText: false });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws, { raw: false, dateNF: 'dd/mm/yyyy' });

                if (data.length === 0) {
                    toast.warning('File trống');
                    return;
                }

                setIsLoading(true);

                const parseExcelDate = (dateValue) => {
                    if (!dateValue) return new Date().toISOString().split('T')[0];
                    if (dateValue instanceof Date) {
                        const offset = dateValue.getTimezoneOffset();
                        const localDate = new Date(dateValue.getTime() - (offset * 60 * 1000));
                        return localDate.toISOString().split('T')[0];
                    }
                    if (typeof dateValue === 'string' && dateValue.includes('/')) {
                        const parts = dateValue.split('/');
                        if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
                    }
                    if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) return dateValue;
                    return new Date().toISOString().split('T')[0];
                };

                // Validate Customers
                const validCustomers = new Set(customers.map(c => (c.TEN_KHACHHANG || c.TEN_KH || c.NCC_KHACHHANG || '').trim().toUpperCase()));

                const groups = {};
                data.forEach(row => {
                    const normalizeRow = {};
                    Object.keys(row).forEach(k => normalizeRow[k.trim().toUpperCase()] = row[k]);

                    const khachHang = normalizeRow['KHÁCH HÀNG'] || normalizeRow['NCC_KHACHHANG'] || normalizeRow['TEN_KHACHHANG'] || normalizeRow['TEN_KH'] || normalizeRow['CUSTOMER'];
                    if (!khachHang) return;

                    let ngayXuat = parseExcelDate(normalizeRow['NGÀY XUẤT'] || normalizeRow['NGAY_XUAT'] || normalizeRow['DATE']);
                    const soPhieu = normalizeRow['SỐ PHIẾU'] || normalizeRow['SOPHIEU'] || normalizeRow['SOPHIEU'] || '';

                    const key = `${khachHang}_${ngayXuat}_${soPhieu || 'AUTO'}`;
                    if (!groups[key]) {
                        groups[key] = {
                            SOPHIEU: soPhieu,
                            NCC_KHACHHANG: khachHang,
                            NGAY_XUAT: ngayXuat,
                            details: [],
                            isValid: true,
                            error: null
                        };
                        // Validation
                        if (!validCustomers.has(khachHang.trim().toUpperCase())) {
                            groups[key].isValid = false;
                            groups[key].error = `Khách hàng chưa tồn tại`;
                        }
                    }

                    const tenHang = normalizeRow['TÊN HÀNG'] || normalizeRow['TEN_HANG'] || normalizeRow['HANG_HOA'];
                    const parseNum = (val) => {
                        if (typeof val === 'number') return val;
                        if (typeof val === 'string') return parseFloat(val.replace(/,/g, '')) || 0;
                        return 0;
                    };
                    const khoiLuong = parseNum(normalizeRow['KHỐI LƯỢNG'] || normalizeRow['KHOILUONG']);
                    const donGia = parseNum(normalizeRow['ĐƠN GIÁ'] || normalizeRow['DONGIA']);
                    const ghiChu = normalizeRow['GHI CHÚ'] || normalizeRow['GHICHU'] || '';

                    if (tenHang) {
                        groups[key].details.push({
                            TEN_HANG: tenHang,
                            KHOILUONG: khoiLuong,
                            DONGIA: donGia,
                            THANHTIEN: khoiLuong * donGia,
                            GHICHU: ghiChu
                        });
                    }
                });

                const preview = Object.values(groups).map(g => ({
                    ...g,
                    tongKhoiLuong: g.details.reduce((s, d) => s + d.KHOILUONG, 0),
                    thanhTien: g.details.reduce((s, d) => s + d.THANHTIEN, 0)
                }));

                // Check for duplicate SOPHIEU within the file
                const soPhieuCount = {};
                preview.forEach(item => {
                    if (item.SOPHIEU) {
                        soPhieuCount[item.SOPHIEU] = (soPhieuCount[item.SOPHIEU] || 0) + 1;
                    }
                });

                // Check against existing vouchers in database
                const existingSoPhieu = new Set(phieuList.map(p => p.SOPHIEU));

                // Mark duplicates as invalid
                preview.forEach(item => {
                    if (item.SOPHIEU) {
                        // Check duplicate within file
                        if (soPhieuCount[item.SOPHIEU] > 1) {
                            item.isValid = false;
                            item.error = `Số phiếu "${item.SOPHIEU}" bị trùng trong file`;
                        }
                        // Check duplicate with existing data
                        else if (existingSoPhieu.has(item.SOPHIEU)) {
                            item.isValid = false;
                            item.error = `Số phiếu "${item.SOPHIEU}" đã tồn tại`;
                        }
                    }
                });

                if (preview.length === 0) toast.warning('Không có dữ liệu hợp lệ');
                else setImportPreview(preview);

            } catch (err) {
                console.error(err);
                toast.error('Lỗi: ' + err.message);
            } finally {
                setIsLoading(false);
            }
        };
        reader.readAsBinaryString(file);
    };







    return (
        <div className="p-3 bg-gray-50 min-h-screen font-sans text-gray-800">
            <div className="mx-auto">
                {/* Header Section */}
                <div className="bg-white rounded-xl shadow-sm p-4 mb-4 border border-purple-100">
                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-4 gap-3">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg shadow-purple-200">
                                <Layers className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-gray-800">Quản Lý Xuất Phụ Phẩm</h1>
                                <p className="text-xs text-gray-500 mt-0.5">Theo dõi phiếu xuất và doanh thu phụ phẩm</p>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={fetchData} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium text-gray-600">
                                <RefreshCw className="w-4 h-4" /> <span className="hidden sm:inline">Làm mới</span>
                            </button>

                            <button onClick={() => setShowImportModal(true)} className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm transition-all shadow-md shadow-green-200 flex items-center gap-2">
                                <Upload className="w-4 h-4" />
                                <span className="hidden sm:inline">Nhập Excel</span>
                            </button>

                            <button
                                onClick={() => handleOpenModal()}
                                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors shadow-md shadow-purple-200 flex items-center gap-2 text-sm font-medium"
                            >
                                <Plus className="w-4 h-4" /> Tạo phiếu xuất
                            </button>
                        </div>
                    </div>

                    {/* Statistics Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white shadow-lg shadow-purple-200">
                            <div className="flex justify-between items-start">
                                <div>
                                    <p className="text-purple-100 text-sm font-medium">Tổng số phiếu</p>
                                    <h3 className="text-3xl font-bold mt-1">{stats.totalVouchers}</h3>
                                </div>
                                <div className="p-2 bg-white/20 rounded-lg"><FileText className="w-6 h-6 text-white" /></div>
                            </div>
                        </div>
                        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white shadow-lg shadow-blue-200">
                            <div className="flex justify-between items-start">
                                <div>
                                    <p className="text-blue-100 text-sm font-medium">Tổng khối lượng</p>
                                    <h3 className="text-3xl font-bold mt-1">{formatNumber(stats.totalVolume, 2)} <span className="text-lg">KG</span></h3>
                                </div>
                                <div className="p-2 bg-white/20 rounded-lg"><Package className="w-6 h-6 text-white" /></div>
                            </div>
                        </div>
                        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white shadow-lg shadow-green-200">
                            <div className="flex justify-between items-start">
                                <div>
                                    <p className="text-green-100 text-sm font-medium">Tổng doanh thu</p>
                                    <h3 className="text-3xl font-bold mt-1">{formatNumber(stats.totalRevenue, 0).replace(/\./g, ',')} <span className="text-lg">₫</span></h3>
                                </div>
                                <div className="p-2 bg-white/20 rounded-lg"><TrendingUp className="w-6 h-6 text-white" /></div>
                            </div>
                        </div>
                    </div>

                    {/* Search and Filter */}
                    {/* Search and Filter */}
                    <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-4">
                        <div className="relative w-full md:w-64">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Tìm kiếm phiếu xuất..."
                                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>

                        <div className="w-full md:w-auto">
                            <div className="bg-white border border-gray-300 rounded-lg p-1.5 shadow-sm flex flex-col sm:flex-row gap-2 sm:gap-0 sm:items-center">
                                <div className="flex items-center gap-2 px-2">
                                    <Calendar className="w-4 h-4 text-gray-500 shrink-0" />
                                    <span className="text-xs text-gray-500 whitespace-nowrap">Từ</span>
                                    <input
                                        type="date"
                                        value={dateRange.startDate}
                                        onChange={e => setDateRange({ ...dateRange, startDate: e.target.value })}
                                        className="text-sm border-none focus:ring-0 p-0 text-gray-700 w-full sm:w-auto bg-transparent"
                                    />
                                </div>
                                <div className="hidden sm:block w-px h-4 bg-gray-300 mx-2"></div>
                                <div className="flex items-center gap-2 px-2 border-t border-gray-100 sm:border-0 pt-2 sm:pt-0">
                                    <span className="text-xs text-gray-500 whitespace-nowrap sm:hidden">Đến ngày</span>
                                    <span className="text-xs text-gray-500 whitespace-nowrap hidden sm:inline">Đến</span>
                                    <input
                                        type="date"
                                        value={dateRange.endDate}
                                        onChange={e => setDateRange({ ...dateRange, endDate: e.target.value })}
                                        className="text-sm border-none focus:ring-0 p-0 text-gray-700 w-full sm:w-auto bg-transparent"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Desktop Table View */}
                    <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => requestSort('SOPHIEU')}>Số phiếu</th>
                                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => requestSort('NGAY_XUAT')}>Ngày xuất</th>
                                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => requestSort('NCC_KHACHHANG')}>Khách hàng</th>
                                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => requestSort('TONG_KHOILUONG')}>Tổng KL (KG)</th>
                                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => requestSort('TONGTIEN')}>Tổng tiền</th>
                                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">Thao tác</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {isLoading ? (
                                    <tr><td colSpan="6" className="text-center py-4 text-sm text-gray-500">Đang tải dữ liệu...</td></tr>
                                ) : currentItems.length > 0 ? (
                                    currentItems.map((item, index) => (
                                        <tr key={index} className="hover:bg-purple-50 transition-colors">
                                            <td className="px-4 py-2 text-sm font-medium text-gray-900">{item.SOPHIEU}</td>
                                            <td className="px-4 py-2 text-sm text-gray-600">{formatDate(item.NGAY_XUAT)}</td>
                                            <td className="px-4 py-2 text-sm text-gray-600">{item.NCC_KHACHHANG}</td>
                                            <td className="px-4 py-2 text-sm font-medium text-blue-600 text-right">{formatNumber(item.TONG_KHOILUONG, 2)}</td>
                                            <td className="px-4 py-2 text-sm font-bold text-green-600 text-right">{formatCurrency(item.TONGTIEN)}</td>
                                            <td className="px-4 py-2 text-center">
                                                <div className="flex justify-center gap-2">
                                                    <button onClick={() => handleOpenModal(item)} className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded transition-colors"><Edit className="w-4 h-4" /></button>
                                                    <button onClick={() => { setPhieuToDelete(item); setShowDeleteConfirmation(true); }} className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"><Trash className="w-4 h-4" /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan="6" className="text-center py-8 text-gray-500">Không có dữ liệu</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile Card View */}
                    <div className="md:hidden space-y-4">
                        {isLoading ? (
                            <div className="text-center py-8 text-gray-500">Đang tải dữ liệu...</div>
                        ) : currentItems.length > 0 ? (
                            currentItems.map((item, index) => (
                                <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 active:scale-[0.98] transition-transform">
                                    <div className="flex justify-between items-start mb-3">
                                        <div className="flex items-center gap-2">
                                            <div className="p-2 bg-purple-50 rounded-lg">
                                                <FileText className="w-5 h-5 text-purple-600" />
                                            </div>
                                            <div>
                                                <span className="block text-sm font-bold text-gray-800">{item.SOPHIEU}</span>
                                                <span className="text-xs text-gray-500 flex items-center gap-1">
                                                    <Calendar className="w-3 h-3" /> {formatDate(item.NGAY_XUAT)}
                                                </span>
                                            </div>
                                        </div>
                                        {/* Actions */}
                                        <div className="flex gap-1">
                                            <button onClick={() => handleOpenModal(item)} className="p-2 text-indigo-600 bg-indigo-50 rounded-lg"><Edit className="w-4 h-4" /></button>
                                            <button onClick={() => { setPhieuToDelete(item); setShowDeleteConfirmation(true); }} className="p-2 text-red-600 bg-red-50 rounded-lg"><Trash className="w-4 h-4" /></button>
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Khách hàng</p>
                                        <p className="text-sm font-semibold text-gray-800 line-clamp-2">{item.NCC_KHACHHANG}</p>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100">
                                        <div>
                                            <p className="text-xs text-gray-500 mb-1">Khối lượng</p>
                                            <p className="text-sm font-bold text-blue-600">{formatNumber(item.TONG_KHOILUONG, 2)} KG</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-xs text-gray-500 mb-1">Thành tiền</p>
                                            <p className="text-sm font-bold text-green-600">{formatCurrency(item.TONGTIEN)}</p>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-8 text-gray-500 bg-white rounded-xl border border-gray-200">Không có dữ liệu</div>
                        )}
                    </div>

                    {/* Pagination Controls */}
                    <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-4">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                            <span>Hiển thị</span>
                            <select
                                value={itemsPerPage}
                                onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                                className="border border-gray-300 rounded px-2 py-1 focus:ring-purple-500 focus:border-purple-500"
                            >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                            <span>phiếu / trang</span>
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="p-2 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <span className="text-sm text-gray-600">
                                Trang {currentPage} / {totalPages || 1}
                            </span>
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages || totalPages === 0}
                                className="p-2 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4 overflow-y-auto">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[95vh] flex flex-col animate-fadeIn">
                        {/* Modal Header */}
                        <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-purple-50 rounded-t-xl">
                            <h2 className="text-lg font-bold text-purple-900 flex items-center gap-2">
                                <FileText className="w-5 h-5" />
                                {isEditMode ? 'Cập Nhật Phiếu Xuất' : 'Tạo Phiếu Xuất Mới'}
                            </h2>
                            <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700"><X className="w-5 h-5" /></button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4">
                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                                {/* Left Side: General Info */}
                                <div className="lg:col-span-4 space-y-4">
                                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 shadow-sm">
                                        <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2 text-sm uppercase tracking-wide">
                                            <AlertCircle className="w-4 h-4 text-purple-600" />
                                            Thông tin chung
                                        </h3>

                                        <div className="space-y-3">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">Số phiếu</label>
                                                <input
                                                    type="text"
                                                    value={currentPhieu.SOPHIEU}
                                                    onChange={e => setCurrentPhieu({ ...currentPhieu, SOPHIEU: e.target.value })}
                                                    placeholder="Tự động tạo (Nếu để trống)"
                                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">Ngày xuất <span className="text-red-500">*</span></label>
                                                <input
                                                    type="date"
                                                    value={currentPhieu.NGAY_XUAT}
                                                    onChange={e => setCurrentPhieu({ ...currentPhieu, NGAY_XUAT: e.target.value })}
                                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                                />
                                            </div>
                                            <div className="relative">
                                                <label className="block text-xs font-medium text-gray-700 mb-1">Khách hàng <span className="text-red-500">*</span></label>
                                                <input
                                                    type="text"
                                                    value={customerSearchTerm}
                                                    onChange={e => {
                                                        setCustomerSearchTerm(e.target.value);
                                                        // if cleared, update state
                                                        if (!e.target.value) setCurrentPhieu({ ...currentPhieu, NCC_KHACHHANG: '' });
                                                    }}
                                                    onFocus={() => setShowCustomerDropdown(true)}
                                                    onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 200)}
                                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                                    placeholder="Tìm kiếm khách hàng..."
                                                />
                                                {showCustomerDropdown && filteredCustomers.length > 0 && (
                                                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                                                        {filteredCustomers.map((c, i) => (
                                                            <div
                                                                key={i}
                                                                className="px-3 py-2 hover:bg-purple-50 cursor-pointer text-sm"
                                                                onClick={() => {
                                                                    setCurrentPhieu({ ...currentPhieu, NCC_KHACHHANG: c.TEN_KHACHHANG });
                                                                    setCustomerSearchTerm(c.TEN_KHACHHANG);
                                                                    setShowCustomerDropdown(false);
                                                                }}
                                                            >
                                                                {c.TEN_KHACHHANG}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">Ghi chú</label>
                                                <textarea
                                                    rows={3}
                                                    value={currentPhieu.GHICHU}
                                                    onChange={e => setCurrentPhieu({ ...currentPhieu, GHICHU: e.target.value })}
                                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 resize-none"
                                                />
                                            </div>
                                        </div>

                                        <div className="mt-4 pt-4 border-t border-gray-200">
                                            <div className="flex justify-between items-center mb-2">
                                                <span className="text-xs text-gray-600">Tổng khối lượng:</span>
                                                <span className="font-bold text-blue-600">{formatNumber(currentPhieu.TONG_KHOILUONG, 2)} KG</span>
                                            </div>
                                            <div className="flex justify-between items-center mb-2">
                                                <span className="text-xs text-gray-600">Thành tiền:</span>
                                                <span className="font-medium text-gray-800">{formatCurrency(currentPhieu.THANHTIEN)}</span>
                                            </div>
                                            <div className="flex justify-between items-center mb-2 gap-2">
                                                <span className="text-xs text-gray-600 flex items-center">VAT (%):</span>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="number"
                                                        value={currentPhieu['PT VAT']}
                                                        onChange={e => setCurrentPhieu({ ...currentPhieu, 'PT VAT': e.target.value })}
                                                        className="w-12 px-1 py-0.5 text-xs border border-gray-300 rounded text-center"
                                                    />
                                                    <span className="text-xs font-medium text-right min-w-[80px]">{formatCurrency(currentPhieu['TIEN VAT'])}</span>
                                                </div>
                                            </div>
                                            <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                                                <span className="text-sm font-bold text-gray-800">Tổng cộng:</span>
                                                <span className="text-lg font-bold text-green-600">{formatCurrency(currentPhieu.TONGTIEN)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Right Side: Details */}
                                <div className="lg:col-span-8">
                                    <div className="bg-white rounded-xl border border-gray-200 h-full flex flex-col">
                                        <div className="p-4 bg-gray-50 border-b border-gray-200 rounded-t-xl">
                                            <div className="flex justify-between items-center mb-3">
                                                <h3 className="font-semibold text-gray-800 flex items-center gap-2 text-sm uppercase tracking-wide">
                                                    <Package className="w-4 h-4 text-blue-600" />
                                                    Chi tiết hàng xuất
                                                </h3>
                                                <button
                                                    onClick={handleAddEmptyRow}
                                                    className="px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-purple-500 hover:text-purple-600 transition-colors flex items-center justify-center gap-2 font-medium text-sm"
                                                >
                                                    <Plus className="w-4 h-4" /> Thêm dòng mới
                                                </button>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-1 gap-3">
                                                <div className="border border-gray-200 rounded-lg overflow-visible">
                                                    <table className="w-full divide-y divide-gray-200">
                                                        <thead className="bg-gray-50">
                                                            <tr>
                                                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 w-10">STT</th>
                                                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 w-32">Tên hàng <span className="text-red-500">*</span></th>
                                                                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 w-24">Khối lượng</th>
                                                                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 w-32">Đơn giá</th>
                                                                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 w-32">Thành tiền</th>
                                                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Ghi chú</th>
                                                                <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600 w-16">Thao tác</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="bg-white divide-y divide-gray-200">
                                                            {chiTietList.map((item, index) => (
                                                                <tr key={index}>
                                                                    <td className="px-3 py-2 text-center text-xs text-gray-500">{index + 1}</td>
                                                                    <td className="px-3 py-2 relative">
                                                                        <input
                                                                            type="text"
                                                                            value={item.TEN_HANG}
                                                                            onChange={e => handleDetailChange(index, 'TEN_HANG', e.target.value)}
                                                                            onFocus={() => setActiveProductDropdownIndex(index)}
                                                                            onBlur={() => setTimeout(() => setActiveProductDropdownIndex(null), 200)}
                                                                            placeholder="Nhập tên hàng..."
                                                                            className="w-full text-sm border border-gray-400 rounded focus:ring-purple-500 focus:border-purple-500 px-2 py-1.5"
                                                                        />
                                                                        {activeProductDropdownIndex === index && (
                                                                            <div className="absolute z-[100] left-0 top-full mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                                                                                {phuPhamList
                                                                                    .filter(p => p.TEN_HANG?.toLowerCase().includes(item.TEN_HANG?.toLowerCase() || ''))
                                                                                    .map((p, i) => (
                                                                                        <div
                                                                                            key={i}
                                                                                            className="px-3 py-2 hover:bg-purple-50 cursor-pointer text-sm"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation(); // Prevent blur from firing too early if possible, though timeout handles it
                                                                                                handleDetailChange(index, 'TEN_HANG', p.TEN_HANG); // Will trigger auto-fill price logic in handleDetailChange
                                                                                            }}
                                                                                        >
                                                                                            {p.TEN_HANG}
                                                                                            <span className="block text-xs text-gray-400">{formatNumber(p.DONGIA)} đ</span>
                                                                                        </div>
                                                                                    ))}
                                                                                {phuPhamList.filter(p => p.TEN_HANG?.toLowerCase().includes(item.TEN_HANG?.toLowerCase() || '')).length === 0 && (
                                                                                    <div className="px-3 py-2 text-sm text-gray-500 italic">Không tìm thấy</div>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                    <td className="px-3 py-2">
                                                                        <input
                                                                            type="number"
                                                                            value={item.KHOILUONG}
                                                                            onChange={e => handleDetailChange(index, 'KHOILUONG', e.target.value)}
                                                                            className="w-full text-right text-sm border border-gray-400 rounded focus:ring-purple-500 focus:border-purple-500 px-2 py-1.5"
                                                                        />
                                                                    </td>
                                                                    <td className="px-3 py-2">
                                                                        <input
                                                                            type="number"
                                                                            value={item.DONGIA}
                                                                            onChange={e => handleDetailChange(index, 'DONGIA', e.target.value)}
                                                                            className="w-full text-right text-sm border border-gray-400 rounded focus:ring-purple-500 focus:border-purple-500 px-2 py-1.5"
                                                                        />
                                                                    </td>
                                                                    <td className="px-3 py-2 text-right text-sm font-medium">
                                                                        {formatNumber(item.THANHTIEN)}
                                                                    </td>
                                                                    <td className="px-3 py-2">
                                                                        <input
                                                                            type="text"
                                                                            value={item.GHICHU || ''}
                                                                            onChange={e => handleDetailChange(index, 'GHICHU', e.target.value)}
                                                                            placeholder="..."
                                                                            className="w-full text-sm border border-gray-400 rounded focus:ring-purple-500 focus:border-purple-500 px-2 py-1.5"
                                                                        />
                                                                    </td>
                                                                    <td className="px-3 py-2 text-center">
                                                                        <div className="flex items-center justify-center gap-1">
                                                                            <button onClick={handleAddEmptyRow} className="text-blue-500 hover:text-blue-700 p-1 rounded-full hover:bg-blue-50" title="Thêm dòng">
                                                                                <Plus className="w-4 h-4" />
                                                                            </button>
                                                                            <button onClick={() => handleRemoveChiTiet(index)} className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50" title="Xóa dòng">
                                                                                <X className="w-4 h-4" />
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex-1 overflow-x-auto p-0">
                                            {/* (Removed duplicate table) */}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex justify-end gap-3">
                            <button onClick={() => setShowModal(false)} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-medium">Hủy bỏ</button>
                            <button
                                onClick={handleSave}
                                disabled={isSubmitting}
                                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium shadow-lg shadow-purple-200 flex items-center gap-2 "
                            >
                                {isSubmitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                                Lưu phiếu
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirm */}
            {showDeleteConfirmation && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-lg w-full max-w-sm p-6 text-center">
                        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                            <Trash className="w-6 h-6 text-red-600" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Xóa Phiếu Xuất?</h3>
                        <p className="text-gray-500 mb-6 text-sm">Hành động này không thể hoàn tác. Bạn có chắc chắn muốn xóa phiếu <span className="font-bold text-gray-800">{phieuToDelete?.SOPHIEU}</span>?</p>
                        <div className="flex gap-3 justify-center">
                            <button onClick={() => setShowDeleteConfirmation(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex-1 font-medium">Hủy</button>
                            <button onClick={handleDelete} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex-1 font-medium">Xóa bỏ</button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: scale(0.95); }
                    to { opacity: 1; transform: scale(1); }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.2s ease-out;
                }
            `}</style>
            {/* Import Modal */}
            {showImportModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className="border-b border-gray-100 p-5 flex justify-between items-center bg-gray-50">
                            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                                <FileSpreadsheet className="w-5 h-5 text-green-600" />
                                {importPreview ? 'Review Dữ liệu' : 'Nhập dữ liệu Excel'}
                            </h3>
                            <button onClick={handleCloseImportModal} className="text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full p-1 transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {isLoading ? (
                            <div className="flex flex-col items-center justify-center h-[400px] space-y-4">
                                <RefreshCw className="w-12 h-12 text-indigo-600 animate-spin" />
                                <p className="text-gray-600 font-medium">Đang xử lý file Excel...</p>
                            </div>
                        ) : importPreview ? (
                            <div className="flex flex-col h-[600px]">
                                <div className="flex-1 overflow-y-auto p-4">
                                    <div className="mb-4 flex items-center gap-4 bg-gray-50 p-3 rounded-lg border border-gray-200">
                                        <div className="flex items-center gap-2">
                                            <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                            <span className="text-sm font-medium">Hợp lệ: <b>{importPreview.filter(i => i.isValid).length}</b></span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                            <span className="text-sm font-medium">Lỗi: <b>{importPreview.filter(i => !i.isValid).length}</b></span>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        {importPreview.map((item, idx) => (
                                            <div key={idx} className={`border rounded-lg overflow-hidden ${item.isValid ? 'border-gray-200 bg-white' : 'border-red-300 bg-red-50'}`}>
                                                {/* Header */}
                                                <div className={`p-3 flex items-center justify-between ${item.isValid ? 'bg-gray-50' : 'bg-red-100'}`}>
                                                    <div className="flex items-center gap-4">
                                                        <div>
                                                            <span className="text-xs text-gray-500">Số phiếu:</span>
                                                            <p className="font-bold text-gray-800">{item.SOPHIEU || '(Tự động tạo)'}</p>
                                                        </div>
                                                        <div>
                                                            <span className="text-xs text-gray-500">Khách hàng:</span>
                                                            <p className="font-semibold text-gray-800">{item.NCC_KHACHHANG}</p>
                                                        </div>
                                                        <div>
                                                            <span className="text-xs text-gray-500">Ngày:</span>
                                                            <p className="font-medium text-gray-700">{formatDate(item.NGAY_XUAT)}</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                        <div className="text-right">
                                                            <span className="text-xs text-gray-500">Tổng tiền:</span>
                                                            <p className="font-bold text-green-600 text-lg">{formatCurrency(item.thanhTien)}</p>
                                                        </div>
                                                        {item.isValid ? (
                                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800">
                                                                <CheckCircle className="w-3 h-3 mr-1" /> OK
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800" title={item.error}>
                                                                <AlertCircle className="w-3 h-3 mr-1" /> {item.error}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Details Table */}
                                                <div className="p-3">
                                                    <table className="min-w-full text-xs">
                                                        <thead className="bg-gray-100">
                                                            <tr>
                                                                <th className="px-2 py-1 text-left">STT</th>
                                                                <th className="px-2 py-1 text-left">Tên hàng</th>
                                                                <th className="px-2 py-1 text-right">Khối lượng</th>
                                                                <th className="px-2 py-1 text-right">Đơn giá</th>
                                                                <th className="px-2 py-1 text-right">Thành tiền</th>
                                                                <th className="px-2 py-1 text-left">Ghi chú</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-gray-200">
                                                            {item.details.map((detail, detailIdx) => (
                                                                <tr key={detailIdx} className="hover:bg-gray-50">
                                                                    <td className="px-2 py-1 text-gray-600">{detailIdx + 1}</td>
                                                                    <td className="px-2 py-1 font-medium">{detail.TEN_HANG}</td>
                                                                    <td className="px-2 py-1 text-right">{formatNumber(detail.KHOILUONG, 2)}</td>
                                                                    <td className="px-2 py-1 text-right">{formatCurrency(detail.DONGIA)}</td>
                                                                    <td className="px-2 py-1 text-right font-semibold text-green-600">{formatCurrency(detail.THANHTIEN)}</td>
                                                                    <td className="px-2 py-1 text-gray-500 text-xs">{detail.GHICHU}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                        <tfoot className="bg-gray-50 font-bold">
                                                            <tr>
                                                                <td colSpan="2" className="px-2 py-2 text-right">Tổng cộng:</td>
                                                                <td className="px-2 py-2 text-right">{formatNumber(item.tongKhoiLuong, 2)}</td>
                                                                <td className="px-2 py-2"></td>
                                                                <td className="px-2 py-2 text-right text-green-600">{formatCurrency(item.thanhTien)}</td>
                                                                <td className="px-2 py-2"></td>
                                                            </tr>
                                                        </tfoot>
                                                    </table>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
                                    <button onClick={() => setImportPreview(null)} className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 font-medium text-sm">Quay lại</button>
                                    <button
                                        onClick={handleImportConfirm}
                                        disabled={importPreview.filter(i => i.isValid).length === 0}
                                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <CheckCircle className="w-4 h-4" /> Xác nhận nhập ({importPreview.filter(i => i.isValid).length})
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="p-6 space-y-6">
                                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                                    <h4 className="font-semibold text-blue-800 text-sm mb-2 flex items-center gap-2">
                                        <AlertCircle className="w-4 h-4" /> Hướng dẫn
                                    </h4>
                                    <ul className="text-sm text-blue-700 space-y-1 ml-5 list-disc">
                                        <li>Tải file mẫu về máy để xem định dạng chuẩn.</li>
                                        <li>Nhập dữ liệu vào file mẫu nhưng <strong>không thay đổi tên cột</strong>.</li>
                                        <li>Hệ thống sẽ <strong>tự động kiểm tra Khách hàng</strong> có tồn tại hay không.</li>
                                        <li>Các dòng cùng Khách hàng & Ngày sẽ gom thành 1 phiếu.</li>
                                    </ul>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <button
                                        onClick={handleDownloadTemplate}
                                        className="flex flex-col items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-green-500 hover:bg-green-50 transition-all group"
                                    >
                                        <div className="p-3 bg-green-100 text-green-600 rounded-full group-hover:scale-110 transition-transform">
                                            <Download className="w-6 h-6" />
                                        </div>
                                        <span className="font-medium text-gray-700 group-hover:text-green-700">1. Tải file mẫu</span>
                                    </button>

                                    <label className="flex flex-col items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-purple-500 hover:bg-purple-50 transition-all cursor-pointer group">
                                        <div className="p-3 bg-purple-100 text-purple-600 rounded-full group-hover:scale-110 transition-transform">
                                            <Upload className="w-6 h-6" />
                                        </div>
                                        <span className="font-medium text-gray-700 group-hover:text-purple-700">2. Chọn file Excel</span>
                                        <input type="file" accept=".xlsx, .xls" className="hidden" onChange={handleFileUploadWrapped} />
                                    </label>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default XuatPhuPhamManagement;
